import { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { showSuccessToast } from '../../../shared/utils/dialogs';

const todayISO = () => new Date().toISOString().slice(0, 10);

const emptyForm = {
  name: '',
  category: '',
  amount: '',
  expense_date: todayISO(),
  voucher_number: '',
  recipient: '',
  status: 'Planned',
  remarks: '',
};

function ProjectFormModal({ project, onClose, onSubmit }) {
  const [form, setForm] = useState(() => (project ? { ...emptyForm, ...project } : emptyForm));
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const set = (field) => (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const amount = Number(form.amount);
    if (!form.amount || Number.isNaN(amount) || amount < 0) {
      setError('Enter a valid amount.');
      return;
    }

    setSaving(true);
    try {
      await onSubmit({
        name: form.name,
        category: form.category || null,
        amount,
        expense_date: form.expense_date,
        voucher_number: form.voucher_number || null,
        recipient: form.recipient || null,
        status: form.status,
        remarks: form.remarks || null,
      });
      showSuccessToast(project ? 'Disbursement updated successfully' : 'Disbursement added successfully');
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to save project');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" />
      <form
        onSubmit={handleSubmit}
        className="relative w-full max-w-3xl bg-white rounded-lg shadow-xl max-h-[70vh] overflow-hidden flex flex-col"
      >
        <div className="shrink-0 flex items-center justify-between px-5 py-4 bg-navy-light">
          <h2 className="text-lg font-semibold text-white">{project ? 'Edit Project' : 'Add Project'}</h2>
          <button type="button" onClick={onClose} className="p-1 text-gray-200 hover:text-white">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {error && (
            <p className="text-sm text-danger bg-danger/10 border border-danger/30 rounded px-3 py-2">{error}</p>
          )}

          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Project / Expense Name</label>
            <input
              required
              value={form.name}
              onChange={set('name')}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-lg"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">Category</label>
              <input
                value={form.category}
                onChange={set('category')}
                placeholder="e.g. Infrastructure"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-lg"
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">Amount (₱)</label>
              <input
                required
                type="number"
                min="0"
                step="0.01"
                value={form.amount}
                onChange={set('amount')}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-lg"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">Date</label>
              <input
                required
                type="date"
                value={form.expense_date}
                onChange={set('expense_date')}
                className="w-full lg:max-w-full max-w-[315px] min-w-0 rounded-md border border-gray-300 px-3 py-2 text-lg"
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">Voucher/OR #</label>
              <input
                value={form.voucher_number}
                onChange={set('voucher_number')}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-lg"
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Recipient</label>
            <input
              value={form.recipient}
              onChange={set('recipient')}
              placeholder="Person or store paid"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-lg"
            />
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Status</label>
            <select
              value={form.status}
              onChange={set('status')}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-lg"
            >
              <option value="Planned">Planned</option>
              <option value="Ongoing">Ongoing</option>
              <option value="Completed">Completed</option>
            </select>
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Remarks</label>
            <textarea
              value={form.remarks}
              onChange={set('remarks')}
              rows={2}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-lg"
            />
          </div>
        </div>

        <div className="shrink-0 flex justify-end gap-2 px-5 py-4 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            className="w-28 py-2 text-center text-lg font-medium text-gray-600 border border-gray-300 hover:text-gray-900"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="w-28 py-2 rounded-md bg-navy-light text-white text-lg font-medium text-center hover:bg-navy disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default ProjectFormModal;
